import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Loan } from '../../types/loan/Loan';
import { roles } from '../../constants';
import { useAppDispatch } from '../../effects/useAppDispatch';
import { loanActions } from '../../actions/loan.actions';
import { routes } from '../../constants/routes';
import { LocationState, LocationStateBuilder, PanelType } from '../../types/state/ui/LocationState';
import { ShowFor } from '../access/ShowFor';
import ContextMenu from '../controls/ContextMenu';
import IconSVG from '../../styles/svg-icons';
import { Confirm } from '../alerts/Confirm';
import { trackingService } from '../../services/action-tracking.service';

interface LoanActionsProps {
    loan: Loan;
}

export function LoanActions({ loan }: LoanActionsProps) {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const locationState = useLocation<LocationState<{ loanId: number }>>().state;

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();

        const isSameLoan = locationState?.panel?.type === PanelType.LoanMonitorSidePanel
            && locationState?.panel?.payload?.loanId === loan.id;

        if (isSameLoan) { // Act as a toggle
            history.replace({
                ...history.location,
                state: new LocationStateBuilder(locationState).resetPanel().result(),
            });
            return;
        }

        history.replace({
            ...history.location,
            state: new LocationStateBuilder<{ loanId: number }>()
                .panel(PanelType.LoanMonitorSidePanel, { loanId: loan.id })
                .result(),
        });
        trackingService.trackAction('EditLoanPanelOpenEvent', {
            loanId: loan.id,
            issuerName: loan.issuerName,
        });
    };

    return (
        <>
            <ShowFor roles={[roles.Administrator, roles.DataEntry]}>
                <ContextMenu
                    menuItems={[{
                        text: 'Edit',
                        action: () => history.push(routes.editLoanUrl(loan.id)),
                    }, {
                        text: 'Delete',
                        action: () => setShowDeleteConfirm(true),
                    }]}
                />
            </ShowFor>
            <ShowFor role={roles.CollateralManager}>
                <button
                    className="btn-link"
                    onClick={handleEditClick}
                >
                    <IconSVG name="edit-pencil" width={16} height={16} />
                </button>
            </ShowFor>
            {showDeleteConfirm && (
                <Confirm
                    text={`Are you sure you want to delete the loan record for ${loan.issuerName}? This action cannot be undone.`}
                    confirmButtonText="Delete"
                    confirmButtonDangerStyle
                    onConfirm={() => {
                        dispatch(loanActions.deleteLoan(loan.id));
                        setShowDeleteConfirm(false);
                    }}
                    onCancel={() => setShowDeleteConfirm(false)}
                />
            )}
        </>
    );
}
